import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@/fontawesome';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-blue-600 hover:bg-blue-700 text-white',
  secondary: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
  ghost: 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
};

const ICON_ONLY_SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'p-1.5 text-sm',
  md: 'p-2 text-sm',
};

const BASE_CLASSES =
  'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors cursor-pointer ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ' +
  'dark:focus-visible:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed';

interface ButtonCommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconDefinition;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  title?: string;
  'aria-label'?: string;
  children?: ReactNode;
  onClick?: () => void;
}

interface ButtonAsButtonProps extends ButtonCommonProps {
  to?: undefined;
  type?: 'button' | 'submit' | 'reset';
}

interface ButtonAsLinkProps extends ButtonCommonProps {
  /** Renders a react-router `<Link>` with identical styling instead of a `<button>`. */
  to: string;
  type?: undefined;
}

export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

export default function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size = 'md',
    icon,
    loading = false,
    disabled = false,
    className = '',
    title,
    children,
    onClick,
  } = props;

  const ariaLabel = props['aria-label'];
  const iconOnly = children === undefined || children === null;
  const sizeClasses = iconOnly ? ICON_ONLY_SIZE_CLASSES[size] : SIZE_CLASSES[size];
  const classes = `${BASE_CLASSES} ${VARIANT_CLASSES[variant]} ${sizeClasses} ${className}`;

  const content = (
    <>
      {loading ? (
        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
      ) : (
        icon !== undefined && <FontAwesomeIcon icon={icon} className="w-4 h-4" />
      )}
      {children}
    </>
  );

  if (props.to !== undefined) {
    // A disabled link cannot be expressed in HTML - render it as an inert span instead.
    if (disabled || loading) {
      return (
        <span className={`${classes} opacity-50 pointer-events-none`} title={title} aria-label={ariaLabel} aria-disabled="true">
          {content}
        </span>
      );
    }
    return (
      <Link to={props.to} className={classes} title={title} aria-label={ariaLabel} onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={props.type ?? 'button'}
      onClick={onClick}
      disabled={disabled || loading}
      className={classes}
      title={title}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  );
}
